import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, Save, X, Lock } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { EmptyState } from '@/components/EmptyState';
import { Modal, ConfirmDialog } from '@/components/Modal';
import { useLabel } from '@/lib/labels';

/** `/settings/node-types` (階層レベル定義タブ、v0.2.5 S-11)
 *  設計: docs/design/v025-config-ui.md §2-2。is_system_reserved 行 (code='store') はラベルのみ編集可。 */

type NodeType = {
  node_type_id: string;
  code: string;
  label: string;
  icon: string | null;
  is_leaf_allowed: boolean;
  is_system_reserved: boolean;
  description: string | null;
  sort_order: number;
};

type EditingRow = { node_type_id: string; label: string; sort_order: number };

export function NodeTypesPage() {
  const t = useLabel();
  const qc = useQueryClient();
  const { data: nodeTypes = [], isLoading } = useQuery<NodeType[]>({
    queryKey: ['node-types'],
    queryFn: () => apiFetch<NodeType[]>('/node-types'),
  });

  const [creating, setCreating] = useState<{ code: string; label: string; sort_order: number } | null>(null);
  const [editing, setEditing] = useState<EditingRow | null>(null);
  const [deleting, setDeleting] = useState<NodeType | null>(null);

  const createMut = useMutation({
    mutationFn: (body: { code: string; label: string; sort_order: number }) =>
      apiFetch('/node-types', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['node-types'] });
      setCreating(null);
      toast.success('階層レベルを追加しました');
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '追加に失敗しました'),
  });

  const updateMut = useMutation({
    mutationFn: (r: EditingRow) => apiFetch(`/node-types/${r.node_type_id}`, {
      method: 'PATCH', body: JSON.stringify({ label: r.label.trim(), sort_order: r.sort_order }),
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['node-types'] });
      // ラベル変更は NodeSwitcher / 組織階層タブ等の全画面に即時反映される (react-query キャッシュ共有のため
      // /nodes のキャッシュも無効化しておく、docs/design/v025-config-ui.md §3-3)
      qc.invalidateQueries({ queryKey: ['nodes'] });
      setEditing(null);
      toast.success('階層レベルを更新しました');
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '更新に失敗しました'),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => apiFetch(`/node-types/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['node-types'] });
      const label = deleting?.label ?? '';
      setDeleting(null);
      toast.success(`階層レベル「${label}」を削除しました`);
    },
    onError: (e) => {
      toast.error(e instanceof ApiClientError ? e.message : '削除に失敗しました');
      setDeleting(null);
    },
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-h2">{t('entity.node_type_definitions', '階層レベル定義')}</h2>
          <p className="text-sm text-neutral-sub mt-1">
            組織ツリーの各段の名称を自社用に定義します。ラベル変更は組織階層・NodeSwitcher・全ダッシュボードに即時反映されます。
          </p>
        </div>
        <button
          type="button"
          onClick={() => setCreating({ code: '', label: '', sort_order: (nodeTypes.reduce((m, nt) => Math.max(m, nt.sort_order), 0) + 10) })}
          className="flex items-center gap-1 px-3 py-2 rounded bg-brand-red text-white text-sm hover:bg-brand-red-hover whitespace-nowrap"
        >
          <Plus size={16} /> レベル追加
        </button>
      </div>

      {isLoading ? <div className="text-neutral-sub">読み込み中…</div> : (
        <div className="border border-neutral-line rounded-lg bg-white overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <thead className="bg-neutral-panel text-neutral-sub">
              <tr>
                <th scope="col" className="text-right px-4 py-2 w-16">{t('field.node_type_definitions.sort_order', '順')}</th>
                <th scope="col" className="text-left px-4 py-2 w-32">{t('field.node_type_definitions.code', 'code')}</th>
                <th scope="col" className="text-left px-4 py-2">{t('field.node_type_definitions.label', 'ラベル')} (自社名称)</th>
                <th scope="col" className="text-left px-4 py-2 w-32">システム予約</th>
                <th scope="col" className="w-24 px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {[...nodeTypes].sort((a, b) => a.sort_order - b.sort_order).map((nt) => {
                const isEditing = editing?.node_type_id === nt.node_type_id;
                return (
                  <tr key={nt.node_type_id} className={`border-t border-neutral-line ${nt.is_system_reserved ? 'bg-neutral-panel/40' : ''}`}>
                    <td className="px-4 py-2 text-right tabular-nums text-neutral-sub">
                      {isEditing ? (
                        <input type="number" min={0} max={9999} value={editing.sort_order}
                          onChange={(e) => setEditing({ ...editing, sort_order: Number(e.target.value) })}
                          className="w-16 px-2 py-1 rounded border border-neutral-line text-right focus-ring" />
                      ) : nt.sort_order}
                    </td>
                    <td className="px-4 py-2 font-mono text-xs text-neutral-sub whitespace-nowrap">{nt.code}</td>
                    <td className="px-4 py-2">
                      {isEditing ? (
                        <input value={editing.label} autoFocus
                          onChange={(e) => setEditing({ ...editing, label: e.target.value })}
                          className="w-full px-2 py-1 rounded border border-neutral-line focus-ring" />
                      ) : nt.label}
                    </td>
                    <td className="px-4 py-2">
                      {nt.is_system_reserved && (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-misespark-navy/10 text-misespark-navy"
                          aria-describedby={`node-type-reserved-${nt.node_type_id}`}
                        >
                          <Lock size={11} /> システム予約
                          <span id={`node-type-reserved-${nt.node_type_id}`} className="sr-only">
                            システム予約のためラベル以外の変更・削除ができません
                          </span>
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right whitespace-nowrap">
                      {isEditing ? (
                        <>
                          <button type="button" aria-label="保存"
                            onClick={() => updateMut.mutate(editing)}
                            disabled={updateMut.isPending || editing.label.trim() === ''}
                            className="p-1 text-brand-red hover:text-brand-red-hover disabled:opacity-40">
                            <Save size={14} />
                          </button>
                          <button type="button" aria-label="キャンセル" onClick={() => setEditing(null)}
                            className="ml-1 p-1 text-neutral-sub hover:text-neutral-text">
                            <X size={14} />
                          </button>
                        </>
                      ) : (
                        <>
                          <button type="button" aria-label="編集"
                            onClick={() => setEditing({ node_type_id: nt.node_type_id, label: nt.label, sort_order: nt.sort_order })}
                            className="p-1 text-brand-red hover:text-brand-red-hover">
                            <Pencil size={14} />
                          </button>
                          <button
                            type="button"
                            aria-label="削除"
                            aria-disabled={nt.is_system_reserved}
                            aria-describedby={nt.is_system_reserved ? `node-type-reserved-${nt.node_type_id}` : undefined}
                            disabled={nt.is_system_reserved}
                            onClick={() => setDeleting(nt)}
                            className="ml-1 p-1 text-neutral-sub hover:text-warning-text disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-neutral-sub"
                          >
                            <Trash2 size={14} />
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {nodeTypes.length === 0 && (
            <EmptyState message="階層レベルが登録されていません" description="「レベル追加」から追加してください" />
          )}
        </div>
      )}

      {/* Design MAJOR対応 (2026-08-03レビュー): 自作の fixed/overlay div ではなく共有Modal を使う */}
      <Modal
        open={creating !== null}
        title="階層レベル追加"
        onClose={() => setCreating(null)}
        size="sm"
        footer={creating && (
          <>
            <button type="button" onClick={() => setCreating(null)}
              className="px-4 py-2 rounded border border-neutral-line hover:bg-neutral-panel">キャンセル</button>
            <button type="submit" form="node-type-create-form" disabled={createMut.isPending}
              className="px-4 py-2 rounded bg-brand-red text-white hover:bg-brand-red-hover disabled:opacity-50">
              {createMut.isPending ? '作成中…' : '作成'}
            </button>
          </>
        )}
      >
        {creating && (
          <form id="node-type-create-form"
            onSubmit={(e) => {
              e.preventDefault();
              const code = creating.code.trim();
              const label = creating.label.trim();
              if (!code) { toast.error('コードを入力してください'); return; }
              if (!label) { toast.error('ラベルを入力してください'); return; }
              createMut.mutate({ code, label, sort_order: creating.sort_order });
            }}
            className="space-y-3">
            <div>
              <label className="block text-sm text-neutral-sub mb-1">{t('field.node_type_definitions.code', 'code')}*</label>
              <input required autoFocus value={creating.code}
                onChange={(e) => setCreating({ ...creating, code: e.target.value })}
                pattern="[A-Za-z0-9_-]+"
                placeholder="例：area1"
                className="w-full px-3 py-2 rounded border border-neutral-line focus-ring" />
              <p className="text-xs text-neutral-sub mt-1">半角英数字・ハイフン・アンダースコアのみ。作成後は変更できません</p>
            </div>
            <div>
              <label className="block text-sm text-neutral-sub mb-1">{t('field.node_type_definitions.label', 'ラベル')} (自社名称)*</label>
              <input required value={creating.label}
                onChange={(e) => setCreating({ ...creating, label: e.target.value })}
                placeholder="例：地方、営業ブロック"
                className="w-full px-3 py-2 rounded border border-neutral-line focus-ring" />
            </div>
            <div>
              <label className="block text-sm text-neutral-sub mb-1">{t('field.node_type_definitions.sort_order', '表示順')}</label>
              <input type="number" min={0} max={9999} value={creating.sort_order}
                onChange={(e) => setCreating({ ...creating, sort_order: Number(e.target.value) })}
                className="w-full px-3 py-2 rounded border border-neutral-line focus-ring" />
            </div>
          </form>
        )}
      </Modal>

      <ConfirmDialog
        open={deleting !== null}
        title="階層レベルを削除しますか？"
        destructive
        confirmLabel="削除する"
        loading={deleteMut.isPending}
        onClose={() => { if (!deleteMut.isPending) setDeleting(null); }}
        onConfirm={() => { if (deleting) deleteMut.mutate(deleting.node_type_id); }}
        message={deleting && (
          <div className="space-y-2">
            <p>階層レベル「{deleting.label}」を削除します。この操作は<strong className="text-danger-text">取り消せません</strong>。</p>
            <p className="text-xs text-neutral-sub">既にこのレベルを使用しているノードがある場合は削除できません (サーバー側でチェックされます)。</p>
          </div>
        )}
      />
    </div>
  );
}
